import { useReducer } from "react";
import { MapObjectTooltipProps } from "./map-object-map-tooltip";

export type MapPopupParameters = Omit<MapObjectTooltipProps, "containerElementLeft" | "containerElementRight" | "containerElementTop" | "padding">;

export type MapContainerState = {
    popupParameters: MapPopupParameters;
    mapElementSize: { mapElementHeight: number; mapElementWidth: number };
};

export enum MapContainerUpdateEvent {
    HIDE_POPUP = "HIDE_POPUP",
    SET_CONTINUE_DRAWING = "SET_CONTINUE_DRAWING",
    SHOW_DELETE_OBJECT_MODAL = "SHOW_DELETE_OBJECT_MODAL",
    SET_POPUP_PARAMETERS = "SET_POPUP_PARAMETERS",
    SET_MAP_ELEMENT_SIZE = "SET_MAP_ELEMENT_SIZE",
}

/** Events that don't affect the map container state, but allow for a centralised event system */
export type MapContainerPassThroughEvents = { type: MapContainerUpdateEvent.SHOW_DELETE_OBJECT_MODAL };

export type MapContainerUpdateEvents =
    | MapContainerPassThroughEvents
    | { type: MapContainerUpdateEvent.HIDE_POPUP }
    | { type: MapContainerUpdateEvent.SET_POPUP_PARAMETERS; payload: MapContainerState["popupParameters"] }
    | { type: MapContainerUpdateEvent.SET_MAP_ELEMENT_SIZE; payload: MapContainerState["mapElementSize"] };

export const mapContainerReducer = (state: MapContainerState, event: MapContainerUpdateEvents) => {
    switch (event.type) {
        case MapContainerUpdateEvent.HIDE_POPUP:
            return { ...state, popupParameters: undefined };
        case MapContainerUpdateEvent.SET_POPUP_PARAMETERS:
            return { ...state, popupParameters: event.payload };
        case MapContainerUpdateEvent.SET_MAP_ELEMENT_SIZE: {
            return { ...state, mapElementSize: event.payload };
        }
        default:
            return state;
    }
};

type ExtractEvent<TEvent extends MapContainerPassThroughEvents, T extends TEvent["type"]> = TEvent extends { type: T } ? TEvent : never;

export const useMapContainerReducer = (
    reducer: typeof mapContainerReducer,
    passThroughEvents: {
        [T in MapContainerPassThroughEvents["type"]]: (params: ExtractEvent<MapContainerPassThroughEvents, T>) => void;
    }
) => {
    const [state, dispatch] = useReducer(reducer, {
        popupParameters: undefined,
        mapElementSize: { mapElementHeight: 0, mapElementWidth: 0 },
    });
    const send = (event: MapContainerUpdateEvents) => {
        passThroughEvents[event.type]?.(event);
        dispatch(event);
    };
    return [state, send] as const;
};
